import type { BundleProductLengthLike } from '~/components/ui/BundleQuantityInput';
import type { InitialProductLengthPick } from '~/components/common/productLengthChoice';
import type {
  PlatformProductCategory,
  Quantity,
  UnitCode,
} from '~/graphql/graphql';
import {
  getPropertyStringSafe,
  MISSING_PROPERTY_PLACEHOLDER,
} from '~/utils/properties';
import type { BuyListTarget } from './useMyOpenBuyLists';

export type AddToBuyListLocation = {
  id: string;
};

export type AddToBuyListProductLength = BundleProductLengthLike & {
  id: string;
  lengthDisplayValue: string;
  length: BundleProductLengthLike['length'] & {
    id: string;
    displayValue: string;
  };
  platformProduct: BundleProductLengthLike['platformProduct'] & {
    id: string;
    code?: string | null;
    name: string;
    properties?: unknown;
    category: PlatformProductCategory;
    standardUnitsCode: UnitCode;
  };
};

export function initialPickOf(
  platformProductsLength: AddToBuyListProductLength,
  quantity: Quantity | null,
  piecesPerBundle?: number
): InitialProductLengthPick {
  const { platformProduct, length } = platformProductsLength;
  return {
    product: {
      id: platformProduct.id,
      code: platformProduct.code,
      name: platformProduct.name,
      category: platformProduct.category,
      standardUnitsCode: platformProduct.standardUnitsCode,
      piecesPerBundle: platformProduct.piecesPerBundle,
    },
    length: {
      id: length.id,
      length: {
        id: length.id,
        displayValue: length.displayValue,
        actualLengthInches: length.actualLengthInches,
      },
      platformProductLength: {
        id: platformProductsLength.id,
        isNominal: platformProductsLength.isNominal,
        length: {
          nominalLengthInches: length.nominalLengthInches,
          actualLengthInches: length.actualLengthInches,
        },
        platformProduct: {
          nominalWidthInches: platformProduct.nominalWidthInches,
          actualWidthInches: platformProduct.actualWidthInches,
          nominalThicknessInches: platformProduct.nominalThicknessInches,
          actualThicknessInches: platformProduct.actualThicknessInches,
          actualLengthInches: platformProduct.actualLengthInches,
          piecesPerBundle: platformProduct.piecesPerBundle,
        },
      },
    },
    quantity,
    piecesPerBundle,
  };
}

export function sortBuyListsForLocation(
  buyLists: BuyListTarget[],
  currentLocationId: string
): BuyListTarget[] {
  return [...buyLists].sort((first, second) => {
    const isFirstCurrent = first.location.id === currentLocationId;
    const isSecondCurrent = second.location.id === currentLocationId;
    if (isFirstCurrent !== isSecondCurrent) return isFirstCurrent ? -1 : 1;
    return first.location.code.localeCompare(second.location.code);
  });
}

export function productSummaryLine(
  platformProductsLength: AddToBuyListProductLength
): string {
  const species = getPropertyStringSafe(
    platformProductsLength.platformProduct.properties,
    'species'
  );

  const summary = [
    platformProductsLength.platformProduct.code ?? '',
    species === MISSING_PROPERTY_PLACEHOLDER ? '' : species,
    platformProductsLength.lengthDisplayValue,
  ]
    .filter((part) => part !== '')
    .join(' · ');

  return summary === '' ? platformProductsLength.platformProduct.name : summary;
}
